import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { ICollaboratorData, ReportsHomePageTabType, ReportsPageViewType } from "src/pages/Studies/models/interfaces";
import { sanitizeForLog } from "src/utils/security";
import {
	AutosaveStatusType,
	GenerationStatusType,
	ICreateReportForm,
	IReport,
	ISectionData,
	ReportPublishedStatusType,
	ReportsTemplateType,
	ISectionSelections,
	TransformedReportData,
	IDataSources,
	IReportIteration,
} from "../models/interface";
import { Dayjs } from "dayjs";
import { ITag } from "src/components/Searchbar/interface";
import { IReferenceItem, ReferenceDrawerVisibilityType, StudyType } from "src/pages/NewAskKural/models/interfaces";

interface IReportsState {
	selectedReport: IReport;
	allReports: IReport[];
	selectedTemplate: ReportsTemplateType | null;
	createReportForm: ICreateReportForm;
	isCreateReportModalVisible: boolean;
	currentSection: ISectionData | null;
	allSectionsData: ISectionData[];
	collaborators: ICollaboratorData[];
	activeCollaborators: string[];
	leftPanelExpanded: boolean;
	editMode: "edit" | "preview";
	generationStatus: Record<string, GenerationStatusType>;
	globalGenerationStatus: GenerationStatusType;
	autosaveStatus: {
		lastSaved: Dayjs | string | null;
		status: AutosaveStatusType;
	};
	selectedReportsPageTab: ReportsHomePageTabType;
	selectedReportsPageView: ReportsPageViewType;
	generalGuidelinesSection: ISectionData | null;
	isReportFilterDrawerOpen: boolean;
	masterReportData: TransformedReportData | null;
	reportsDropdownData: { [key: string]: any[] };
	sectionSelections: Record<string, ISectionSelections>; // sectionId -> selections
	activeDataDrawerSection: string | null;
	sectionsContainerExpanded: boolean;
	sectionPreviewIdClick: string;
	addNewSectionTrigger: boolean;
	isReordering: boolean;
	isLockedModalOpen: boolean;
	lockedModalContent: string;
	isLockedByAnotherUser: { status: boolean; user: { user_id: string; user_name: string } | null };
	generateAllClick: boolean;
	reportsSearchText: string;
	reportsSelectedCollaborators: ITag[];
	reportsReferences?: {
		[key in StudyType]?: IReferenceItem[];
	};
	reportsReferenceDrawerVisibility?: ReferenceDrawerVisibilityType;
	reportsReferenceActiveTab: StudyType;
	reportsReferencesStudyType: StudyType[];
	abortStreamingReports: Record<string, AbortController> | null;
	reportIterations: IReportIteration[];
}

export const initialState: IReportsState = {
	selectedReport: {
		report_gen_id: "",
		report_iteration_id: "",
		name: "",
		description: "",
		status: ReportPublishedStatusType.Draft,
		updated_at: "",
		template: {
			template_name: "",
			template_id: "",
		},
		permissions: {
			collaborators: [],
		},
		user_access_type: null,
		file_type: null,
	},
	allReports: [],
	createReportForm: {
		name: "",
		description: "",
		template: {
			template_id: null,
			template_name: null,
		},
		file_type: null,
	},
	selectedTemplate: null,
	isCreateReportModalVisible: false,
	currentSection: {
		section_id: "",
		name: "",
		data: {},
		ai_summary: {
			content: null,
		},
		data_sources: {},
	},
	allSectionsData: [],
	collaborators: [],
	activeCollaborators: [],
	leftPanelExpanded: true,
	editMode: "edit",
	generationStatus: {},
	globalGenerationStatus: GenerationStatusType.Preview,
	autosaveStatus: {
		lastSaved: null,
		status: null,
	},
	selectedReportsPageTab: ReportsHomePageTabType.Recent,
	selectedReportsPageView: ReportsPageViewType.Grid,
	generalGuidelinesSection: {
		section_id: "general-guidelines",
		order: 0,
		name: "General Guidelines",
		data_sources: {},
		data_access_mismatch: false,
	} as ISectionData,
	isReportFilterDrawerOpen: false,
	masterReportData: null,
	reportsDropdownData: {},
	sectionSelections: {},
	activeDataDrawerSection: null,
	sectionsContainerExpanded: false,
	sectionPreviewIdClick: "",
	addNewSectionTrigger: false,
	isReordering: false,
	isLockedModalOpen: false,
	lockedModalContent: "",
	isLockedByAnotherUser: null,
	generateAllClick: false,
	reportsSearchText: null,
	reportsSelectedCollaborators: [],
	reportsReferences: {},
	reportsReferenceDrawerVisibility: ReferenceDrawerVisibilityType.hidden,
	reportsReferenceActiveTab: null,
	reportsReferencesStudyType: [],
	abortStreamingReports: null,
	reportIterations: [],
};

const reportsSlice = createSlice({
	name: "reports",
	initialState,
	reducers: {
		// Existing actions
		setSelectedReport(state, action: PayloadAction<IReport>) {
			state.selectedReport = action.payload;
		},
		updateSelectedReport(state, action: PayloadAction<Partial<IReport>>) {
			const updatedReport = action.payload;
			state.selectedReport = {
				...state.selectedReport,
				...updatedReport,
			};
			const index = state.allReports.findIndex((report) => report.report_gen_id === state.selectedReport.report_gen_id);
			if (index !== -1) {
				state.allReports[index] = { ...state.allReports[index], ...updatedReport };
			}
		},
		setAllReports(state, action: PayloadAction<IReport[]>) {
			state.allReports = action.payload;
		},
		addNewReport(state, action: PayloadAction<IReport>) {
			state.allReports.unshift(action.payload);
		},
		updateReportCollaboratorData(state, action: PayloadAction<{ collab_details: any; report_iteration_id: string }>) {
			const { collab_details, report_iteration_id } = action.payload;
			const report = state.allReports.find((s) => s.report_iteration_id === report_iteration_id);
			if (report && report.permissions) {
				report.permissions.collaborators = collab_details;
			}
			const iterationIndex = state.reportIterations.findIndex((iteration) => iteration._id === report_iteration_id);
			if (iterationIndex !== -1) {
				state.reportIterations[iterationIndex].permissions.collaborators = collab_details;
			}
		},
		deleteSelectedReport(state, action: PayloadAction<{ report_gen_id: string; report_iteration_id?: string }>) {
			const { report_gen_id, report_iteration_id } = action.payload;
			state.allReports = state.allReports.filter((report) => {
				// If report_iteration_id is provided, check both
				if (report_iteration_id) {
					return !(report.report_gen_id === report_gen_id && report.report_iteration_id === report_iteration_id);
				}
				// Otherwise, just check report_gen_id
				return report.report_gen_id !== report_gen_id;
			});
			if (state.selectedReport.report_gen_id === report_gen_id && (!report_iteration_id || state.selectedReport.report_iteration_id === report_iteration_id)) {
				state.selectedReport = initialState.selectedReport;
			}
			state.autosaveStatus = {
				lastSaved: null,
				status: null,
			};
		},
		setSelectedTemplate(state, action: PayloadAction<ReportsTemplateType | null>) {
			state.selectedTemplate = action.payload;
		},
		resetSelectedReport(state) {
			state.selectedReport = initialState.selectedReport;
		},
		toggleLeftPanel(state, action: PayloadAction<boolean | undefined>) {
			if (typeof action.payload === "boolean") {
				state.leftPanelExpanded = action.payload;
			} else {
				state.leftPanelExpanded = !state.leftPanelExpanded;
			}
		},
		setCreateReportFormField(state, action: PayloadAction<{ field: keyof ICreateReportForm; value: any }>) {
			const { field, value } = action.payload;
			state.createReportForm[field] = value;
		},
		setIsCreateReportModalVisible(state, action: PayloadAction<boolean>) {
			state.isCreateReportModalVisible = action.payload;
		},
		setCreateReportForm(state, action: PayloadAction<Partial<ICreateReportForm>>) {
			state.createReportForm = { ...state.createReportForm, ...action.payload };
		},
		updateCreateReportForm(state, action: PayloadAction<Partial<ICreateReportForm>>) {
			Object.entries(action.payload).forEach(([key, value]) => {
				if (key === "template" && typeof value === "object" && value !== null) {
					state.createReportForm.template = {
						...state.createReportForm.template,
						...value,
					};
				} else {
					state.createReportForm[key] = value;
				}
			});
		},
		resetCreateReportForm(state) {
			state.createReportForm = initialState.createReportForm;
		},
		setSelectedReportsPageTab(state, action: PayloadAction<ReportsHomePageTabType>) {
			state.selectedReportsPageTab = action.payload;
		},
		setSelectedReportsPageView(state, action: PayloadAction<ReportsPageViewType>) {
			state.selectedReportsPageView = action.payload;
		},

		// Section management actions
		setCurrentSection(state, action: PayloadAction<ISectionData | null>) {
			state.currentSection = action.payload;
		},
		setAllSectionsData(state, action: PayloadAction<ISectionData[]>) {
			state.allSectionsData = action.payload;
		},
		updateSectionData(state, action: PayloadAction<{ sectionId: string; data: Record<string, any> }>) {
			const { sectionId, data } = action.payload;
			const section = state.allSectionsData.find((s) => s.section_id === sectionId);
			if (section) {
				section.data = { ...section.data, ...data };
				// state.autosaveStatus.status = AutosaveStatusType.UnsavedChanges;
			}
			if (state.currentSection?.section_id === sectionId) {
				state.currentSection.data = { ...state.currentSection.data, ...data };
			}
		},
		addSection(state, action: PayloadAction<ISectionData>) {
			if (!Array.isArray(state.allSectionsData)) {
				state.allSectionsData = [];
			}
			state.allSectionsData.push(action.payload);
			// state.autosaveStatus.status = AutosaveStatusType.UnsavedChanges;
		},
		removeSection(state, action: PayloadAction<string>) {
			const sectionId = action.payload;
			state.allSectionsData = state.allSectionsData.filter((s) => s.section_id !== sectionId);
			if (state.currentSection?.section_id === sectionId) {
				state.currentSection = null;
			}
			// state.autosaveStatus.status = AutosaveStatusType.UnsavedChanges;
		},
		updateGlobalInstructions(state, action: PayloadAction<{ instructions: string; data_sources: IDataSources; data_access_mismatch: boolean }>) {
			const { instructions, data_sources, data_access_mismatch } = action.payload;
			state.generalGuidelinesSection = {
				...state.generalGuidelinesSection,
				instructions: instructions,
				data_access_mismatch: data_access_mismatch,
				data: {
					...state.generalGuidelinesSection?.data,
					instructions: instructions,
				},
				data_sources,
			};
		},
		updateSection(state, action: PayloadAction<ISectionData>) {
			const index = state.allSectionsData.findIndex((s) => s.section_id === action.payload.section_id);
			if (index !== -1) {
				state.allSectionsData[index] = action.payload;
				// state.autosaveStatus.status = AutosaveStatusType.UnsavedChanges;
			}
			if (state.currentSection?.section_id === action.payload.section_id) {
				state.currentSection = action.payload;
			}
		},
		updateSectionName(state, action: PayloadAction<{ sectionId: string; name: string }>) {
			const { sectionId, name } = action.payload;
			const section = state.allSectionsData.find((s) => s.section_id === sectionId);
			if (section) {
				section.name = name;
				// state.autosaveStatus.status = AutosaveStatusType.UnsavedChanges;
			}
			if (state.currentSection?.section_id === sectionId) {
				state.currentSection.name = name;
			}
		},
		updateSectionOrder(state, action: PayloadAction<{ sectionId: string; order: number }>) {
			const { sectionId, order } = action.payload;
			const section = state.allSectionsData.find((s) => s.section_id === sectionId);
			if (section) {
				section.order = order;
				// state.autosaveStatus.status = AutosaveStatusType.UnsavedChanges;
			}
			if (state.currentSection?.section_id === sectionId) {
				state.currentSection.order = order;
			}
		},

		// Collaboration actions
		setCollaborators(state, action: PayloadAction<ICollaboratorData[]>) {
			state.collaborators = action.payload;
		},
		addCollaborator(state, action: PayloadAction<ICollaboratorData>) {
			state.collaborators.push(action.payload);
		},
		removeCollaborator(state, action: PayloadAction<string>) {
			state.collaborators = state.collaborators.filter((c) => c.section_id !== action.payload);
			state.activeCollaborators = state.activeCollaborators.filter((id) => id !== action.payload);
		},
		setActiveCollaborators(state, action: PayloadAction<string[]>) {
			state.activeCollaborators = action.payload;
		},
		addActiveCollaborator(state, action: PayloadAction<string>) {
			if (!state.activeCollaborators.includes(action.payload)) {
				state.activeCollaborators.push(action.payload);
			}
		},
		removeActiveCollaborator(state, action: PayloadAction<string>) {
			state.activeCollaborators = state.activeCollaborators.filter((id) => id !== action.payload);
		},

		// Checkpoint and iteration actions - REMOVED

		// Edit mode actions
		setEditMode(state, action: PayloadAction<"edit" | "preview">) {
			state.editMode = action.payload;
		},
		toggleEditMode(state) {
			state.editMode = state.editMode === "edit" ? "preview" : "edit";
		},

		// Generation status actions
		updatePreviewContentForAllSummary(state, action: PayloadAction<Array<{ id: string; content: string }>>) {
			// action.payload is an array: [{ id: sectionId, content: previewContent }]
			action.payload.forEach(({ id, content }) => {
				const section = state.allSectionsData.find((s) => s.section_id === id);
				if (section) {
					section.ai_summary.content = content;
				}
				if (state.currentSection?.section_id === id) {
					state.currentSection.ai_summary.content = content;
				}
			});
		},
		setGenerationStatus(state, action: PayloadAction<Partial<IReportsState["generationStatus"]>>) {
			state.generationStatus = { ...state.generationStatus, ...action.payload };
		},
		resetGenerationStatus(state) {
			state.generationStatus = initialState.generationStatus;
		},
		updateGenerationProgress(state, action: PayloadAction<GenerationStatusType>) {
			state.generationStatus.progress = action.payload;
		},
		setGlobalGenerationStatus(state, action: PayloadAction<GenerationStatusType>) {
			state.globalGenerationStatus = action.payload;
		},

		// Autosave status actions
		setAutosaveStatus(state, action: PayloadAction<Partial<IReportsState["autosaveStatus"]>>) {
			state.autosaveStatus = { ...state.autosaveStatus, ...action.payload };
		},
		markAsSaved(state) {
			state.autosaveStatus.status = AutosaveStatusType.Saved;
			state.autosaveStatus.lastSaved = new Date().toISOString();
		},
		markAsDirty(state) {
			if (state.autosaveStatus?.status === AutosaveStatusType.ValidationError) return;
			state.autosaveStatus.status = AutosaveStatusType.UnsavedChanges;
		},
		setSaving(state, action: PayloadAction<boolean>) {
			state.autosaveStatus.status = action.payload ? AutosaveStatusType.Saving : AutosaveStatusType.UnsavedChanges;
		},

		// Publishing workflow action
		publishSelectedReport(state, action: PayloadAction<{ report_gen_id: string; report_iteration_id: string }>) {
			// Update the report status or any other publish-related state
			const report = state.allReports.find((r) => r.report_gen_id === action.payload.report_gen_id);
			if (report) {
				report.status = ReportPublishedStatusType.Published;
				report.updated_at = new Date().toISOString();
			}
			if (state.selectedReport.report_gen_id === action.payload.report_gen_id) {
				state.selectedReport.status = ReportPublishedStatusType.Published;
				state.selectedReport.updated_at = new Date().toISOString();
			}
		},

		setIsReportFilterDrawerOpen(state, action: PayloadAction<boolean>) {
			state.isReportFilterDrawerOpen = action.payload;
		},

		setReportsDropdownData(state, action: PayloadAction<{ [key: string]: any[] }>) {
			state.reportsDropdownData = action.payload;
		},

		setActiveDataDrawerSection(state, action: PayloadAction<string | null>) {
			state.activeDataDrawerSection = action.payload;
		},

		clearSectionSelections(state, action: PayloadAction<string>) {
			delete state.sectionSelections[action.payload];
		},

		initializeSectionSelections(state, action: PayloadAction<string>) {
			const sectionId = action.payload;
			if (!state.sectionSelections[sectionId]) {
				state.sectionSelections[sectionId] = {
					studies: [],
					datasets: [],
					tags: [],
				};
			}
		},
		setSectionsContainerExpanded(state, action: PayloadAction<boolean>) {
			state.sectionsContainerExpanded = action.payload;
		},
		updateSectionPreview(state, action: PayloadAction<{ sectionId: string; ai_summary: string }>) {
			const { sectionId, ai_summary } = action.payload;
			// Find the section in allSectionsData
			const section = state.allSectionsData.find((s) => s.section_id === sectionId);
			if (section) {
				if (!section.ai_summary) {
					section.ai_summary = { content: null };
				}
				section.ai_summary.content = ai_summary;
			}
			// Update currentSection if it matches
			if (state.currentSection?.section_id === sectionId) {
				if (!state.currentSection.ai_summary) {
					state.currentSection.ai_summary = { content: null };
				}
				state.currentSection.ai_summary.content = ai_summary;
			}
			// state.autosaveStatus.status = AutosaveStatusType.UnsavedChanges;
		},
		updateSectionDetails(
			state,
			action: PayloadAction<{
				sectionId: string;
				updateData: {
					name?: string;
					data?: {
						key_question?: string;
						instructions?: string;
					};
					data_sources?: IDataSources;
					order?: number;
					type?: string;
				};
			}>
		) {
			const { sectionId, updateData } = action.payload;
			const section = state.allSectionsData.find((s) => s.section_id === sectionId);

			if (section) {
				// Update basic fields
				if (updateData.name !== undefined) section.name = updateData.name;
				if (updateData.order !== undefined) section.order = updateData.order;
				if (updateData.type !== undefined) section.type = updateData.type;
				if (updateData.data_sources !== undefined) section.data_sources = updateData.data_sources;

				// Update data fields
				if (updateData.data) {
					section.data = { ...section.data, ...updateData.data };
				}

				section.updated_at = new Date().toISOString();
				// state.autosaveStatus.status = AutosaveStatusType.UnsavedChanges;
			}

			// Update current section if it matches
			if (state.currentSection?.section_id === sectionId) {
				if (updateData.name !== undefined) state.currentSection.name = updateData.name;
				if (updateData.order !== undefined) state.currentSection.order = updateData.order;
				if (updateData.type !== undefined) state.currentSection.type = updateData.type;
				if (updateData.data_sources !== undefined) state.currentSection.data_sources = updateData.data_sources;

				if (updateData.data) {
					state.currentSection.data = { ...state.currentSection.data, ...updateData.data };
				}

				state.currentSection.updated_at = new Date().toISOString();
			}
		},
		resetSelectedReportState(state) {
			state.selectedReport = initialState.selectedReport;
			state.createReportForm = initialState.createReportForm;
			state.selectedTemplate = initialState.selectedTemplate;
			state.allSectionsData = initialState.allSectionsData;
			state.currentSection = initialState.currentSection;
			state.leftPanelExpanded = initialState.leftPanelExpanded;
			state.generalGuidelinesSection = initialState.generalGuidelinesSection;
			state.isReportFilterDrawerOpen = initialState.isReportFilterDrawerOpen;
			state.masterReportData = initialState.masterReportData;
			state.reportsDropdownData = initialState.reportsDropdownData;
			state.sectionSelections = initialState.sectionSelections;
			state.activeDataDrawerSection = initialState.activeDataDrawerSection;
			state.sectionsContainerExpanded = initialState.sectionsContainerExpanded;
			state.globalGenerationStatus = initialState.globalGenerationStatus;
		},

		// Reset entire state
		resetReportsState(state) {
			return { ...initialState, allReports: state.allReports };
		},

		setSectionPreviewIdClick(state, action: PayloadAction<string>) {
			state.sectionPreviewIdClick = action.payload;
			const section = state.allSectionsData.find((s) => s.section_id === action.payload);
			if (section) {
				section.is_edited = false;
			}
		},
		setAddNewSectionTrigger(state, action: PayloadAction<boolean>) {
			state.addNewSectionTrigger = action.payload;
		},
		setIsReordering(state, action: PayloadAction<boolean>) {
			state.isReordering = action.payload;
		},

		setIsLockedModalOpen(state, action: PayloadAction<boolean>) {
			state.isLockedModalOpen = action.payload;
		},
		setIsLockedByAnotherUser(state, action: PayloadAction<{ status: boolean; user: { user_id: string; user_name: string } | null }>) {
			state.isLockedByAnotherUser = action.payload;
		},
		setLockedModalContent(state, action: PayloadAction<string>) {
			state.lockedModalContent = action.payload;
		},
		updateReportStar(state, action: PayloadAction<{ reportIterationId: string; reportGenId: string; star: boolean; user_id: string }>) {
			const { star, reportIterationId, reportGenId, user_id } = action.payload;
			const report = state.allReports.find((s) => s.report_iteration_id === reportIterationId && s.report_gen_id === reportGenId);
			if (report) {
				report?.permissions?.collaborators?.forEach((collab) => {
					if (collab?.collaborator_id === user_id) {
						collab.is_star = star;
					}
				});
			}
		},
		setGenerateAllClick(state, action: PayloadAction<boolean>) {
			state.generateAllClick = action.payload;
		},
		setReportsSearchText(state, action: PayloadAction<string>) {
			state.reportsSearchText = action.payload;
		},
		setReportsSelectedCollaborators(state, action: PayloadAction<ITag[]>) {
			state.reportsSelectedCollaborators = action.payload;
		},
		setReportsReferences(state, action: PayloadAction<{ [key in StudyType]?: IReferenceItem[] }>) {
			state.reportsReferences = action.payload;
			state.reportsReferenceDrawerVisibility = ReferenceDrawerVisibilityType.hidden;
		},
		updateReportsReferences(state, action: PayloadAction<{ studyType: StudyType; references: IReferenceItem[] }>) {
			const { studyType, references } = action.payload;
			if (!state.reportsReferences) {
				state.reportsReferences = {} as { [key in StudyType]: IReferenceItem[] };
			}
			state.reportsReferences[studyType] = references;
			state.reportsReferenceDrawerVisibility = ReferenceDrawerVisibilityType.hidden;
		},
		resetReportsReferences(state) {
			state.reportsReferences = {};
			state.reportsReferenceDrawerVisibility = ReferenceDrawerVisibilityType.hidden;
		},
		setReportsReferenceDrawerVisibility(state, action: PayloadAction<ReferenceDrawerVisibilityType>) {
			state.reportsReferenceDrawerVisibility = action.payload;
		},
		setReportsReferenceActiveTab(state, action: PayloadAction<StudyType>) {
			state.reportsReferenceActiveTab = action.payload;
		},
		setReportsReferencesStudyType(state, action: PayloadAction<StudyType[]>) {
			state.reportsReferencesStudyType = action.payload;
		},
		resetAllReferencesStates(state) {
			state.reportsReferences = {};
			state.reportsReferenceDrawerVisibility = ReferenceDrawerVisibilityType.hidden;
			state.reportsReferenceActiveTab = null;
			state.reportsReferencesStudyType = [];
		},
		updateAbortStreamingReports(state, action: PayloadAction<{ sectionId: string; controller: AbortController | null }>) {
			if (!state.abortStreamingReports) {
				state.abortStreamingReports = {};
			}
			const { sectionId, controller } = action.payload;
			if (state.abortStreamingReports[sectionId]) {
				try {
					state.abortStreamingReports[sectionId].abort("Replaced by new request");
				} catch (error) {
					console.warn(`Failed to abort controller for section ${sanitizeForLog(sectionId)}:`, error);
				}
			}
			if (controller) {
				state.abortStreamingReports[sectionId] = controller;
			} else {
				delete state.abortStreamingReports[sectionId];
			}
		},
		cleanupAbortControllerReports(state, action: PayloadAction<string>) {
			if (state.abortStreamingReports && state.abortStreamingReports[action.payload]) {
				delete state.abortStreamingReports[action.payload];
			}
		},
		abortAllStreamsReports(state) {
			if (!state.abortStreamingReports) {
				state.abortStreamingReports = {};
			}
			const entries = Object.entries(state.abortStreamingReports);
			if (entries.length > 0) {
				entries.forEach(([sectionId, controller]) => {
					try {
						controller.abort?.("Aborted all streams");
					} catch (error) {
						console.warn(`Failed to abort controller for section_id: ${sanitizeForLog(sectionId)}`, error);
					}
				});
				state.abortStreamingReports = {};
			} else {
				console.warn("No streaming report controllers to abort.");
			}
		},
		setReportIterations(state, action: PayloadAction<IReportIteration[]>) {
			state.reportIterations = action.payload;
		},
	},
});

export const {
	// Existing actions
	setSelectedReport,
	updateSelectedReport,
	resetSelectedReportState,
	setAllReports,
	addNewReport,
	deleteSelectedReport,
	setSelectedTemplate,
	resetSelectedReport,
	toggleLeftPanel,
	setCreateReportFormField,
	setCreateReportForm,
	updateCreateReportForm,
	resetCreateReportForm,
	setIsCreateReportModalVisible,
	setSelectedReportsPageTab,
	setSelectedReportsPageView,
	setIsReportFilterDrawerOpen,

	// Section actions
	updateGlobalInstructions,
	setCurrentSection,
	setAllSectionsData,
	updateSectionData,
	addSection,
	removeSection,
	updateReportCollaboratorData,
	updateSection,
	updateSectionName,
	updateSectionOrder,

	// Collaboration actions
	setCollaborators,
	addCollaborator,
	removeCollaborator,
	setActiveCollaborators,
	addActiveCollaborator,
	removeActiveCollaborator,

	// Checkpoint and iteration actions - REMOVED

	// Edit mode actions
	setEditMode,
	toggleEditMode,
	updateSectionPreview,

	// Generation status actions
	updatePreviewContentForAllSummary,
	setGenerationStatus,
	resetGenerationStatus,
	updateGenerationProgress,
	setGlobalGenerationStatus,

	// Autosave actions
	setAutosaveStatus,
	markAsSaved,
	markAsDirty,
	setSaving,

	// Publishing action
	publishSelectedReport,

	setReportsDropdownData,
	setActiveDataDrawerSection,
	clearSectionSelections,
	initializeSectionSelections,
	setSectionsContainerExpanded,

	// Reset action
	resetReportsState,
	setSectionPreviewIdClick,
	setAddNewSectionTrigger,
	setIsReordering,

	//locked modal
	setIsLockedModalOpen,
	setLockedModalContent,
	setIsLockedByAnotherUser,
	updateReportStar,
	setGenerateAllClick,
	setReportsSearchText,
	setReportsSelectedCollaborators,
	setReportsReferences,
	updateReportsReferences,
	resetReportsReferences,
	setReportsReferenceDrawerVisibility,
	setReportsReferenceActiveTab,
	setReportsReferencesStudyType,
	resetAllReferencesStates,
	updateAbortStreamingReports,
	cleanupAbortControllerReports,
	abortAllStreamsReports,
	setReportIterations,
} = reportsSlice.actions;

export default reportsSlice.reducer;
